import { ChevronRight } from "lucide-react";
import React, { ComponentType, SVGProps, useEffect, useState } from "react";
import { useRouter } from "next/router";
import useStore from "@/shared/store/useStore";
import usePermissionStore from "@/shared/store/usePermissionStore";
import Link from "next/link";
import {
  Sidebar,
  SidebarContent,
  SidebarGroup,
  SidebarGroupContent,
  SidebarGroupLabel,
  SidebarMenu,
  SidebarMenuButton,
  SidebarMenuItem,
  SidebarMenuSub,
  useSidebar,
} from "../ui/sidebar";
import { cn, populateMenu } from "@/shared/lib/utils";
import {
  Collapsible,
  CollapsibleContent,
  CollapsibleTrigger,
} from "../ui/collapsible";
import { Avatar, AvatarFallback, AvatarImage } from "../ui/avatar";
import Footer from "./footer";
import DashboardIcon from "~/icons/dashboard.svg";
import MasterDataIcon from "~/icons/Master-data.svg";
import TransactionIcon from "~/icons/Transaction.svg";
import ProductIcon from "~/icons/Product.svg";
import DriverIcon from "~/icons/Driver.svg";
import ReportIcon from "~/icons/report.svg";
import PicDriverIcon from "~/icons/pic-driver.svg";
import HomeIcon from "~/icons/Home.svg";
import TicketIcon from "~/icons/ticket.svg";

// ----------------- ICON MAP (top-level so it's not recreated per item) -----------------
type IconType = ComponentType<SVGProps<SVGSVGElement>>;

// ----------------- ICON MAP -----------------
const iconMenuMap: Record<string, IconType> = {
  dashboard: DashboardIcon,
  "master-data": MasterDataIcon,
  transaction: TransactionIcon,
  "app-setting": TicketIcon,
  product: ProductIcon,
  driver: DriverIcon,
  report: ReportIcon,
  "pic-driver": PicDriverIcon,
  home: HomeIcon,
};

const getIconComponent = (featureName?: string) => {
  if (!featureName) return DashboardIcon;
  const key = featureName.replace(/\s+/g, "-").toLowerCase();
  return iconMenuMap[key] ?? DashboardIcon;
};
// ----------------- TYPES (local) -----------------
interface ConvertedMenu {
  name: string;
  path: string;
  icon?: IconType;
  children?: { name: string; path: string }[];
}

type ApiChild = {
  feature: string;
  parent_feature: string;
  feature_path: string;
  feature_authorization: string;
};

type ApiParent = {
  feature: string;
  parent_feature: string;
  feature_path: string;
  feature_authorization: string;
  children: ApiChild[];
};

// ----------------- CONVERTER -----------------
const convertMenus = (apiMenus: ApiParent[] | undefined): ConvertedMenu[] => {
  if (!Array.isArray(apiMenus)) return [];

  return apiMenus.map((m) => ({
    name: m.feature,
    path: m.feature_path || "",
    icon: getIconComponent(m.feature),
    children:
      Array.isArray(m.children) && m.children.length > 0
        ? m.children.map((c) => ({
            name: c.feature,
            path: c.feature_path || "",
          }))
        : [],
  }));
};

// ----------------- Sidebar item renderer -----------------
const baseButton =
  "flex gap-2 items-center p-3 rounded-md h-auto min-h-[46px] cursor-pointer";
const activeStyle =
  "text-[#FF0025] font-semibold bg-[#F0F2F4D1] hover:!text-[#FF0025]";
const inactiveStyle =
  "text-[#505D6D] font-normal hover:!text-[#FF0025] hover:bg-gray-100";

// ----------------- AppSidebar (main) -----------------
const AppSidebar = () => {
  const router = useRouter();
  const { setOpenMobile } = useSidebar();
  const { user } = useStore((s) => s);

  // Read features from the in-memory permission store instead of localStorage
  const features = usePermissionStore((s) => s.features);

  const [sidebarState, setSidebarState] = useState<ConvertedMenu[]>([]);

  useEffect(() => {
    // Build menu from permission store features
    const apiMenus = populateMenu({ features }) as ApiParent[];
    const converted = convertMenus(apiMenus);

    // Pastikan Dashboard ada di index 0
    const dashboardExists = converted.some(
      (menu) => menu.name.toLowerCase() === "dashboard"
    );

    const dashboardMenu: ConvertedMenu = {
      name: "Dashboard",
      path: "/dashboard",
      icon: getIconComponent("dashboard"),
      children: [],
    };

    if (!dashboardExists) {
      converted.unshift(dashboardMenu);
    }

    setSidebarState(converted);
  }, [features]);

  return (
    <Sidebar>
      <SidebarContent className="flex flex-col h-full">
        <SidebarGroup className="flex-shrink-0">
          <SidebarGroupLabel className="flex items-center justify-between m-5 mx-0">
            <div className={cn("font-roboto duration-300 transition-all")}>
              <div className="text-xl text-[#001A41] font-semibold">SiGeulis Dashboard</div>
            </div>
          </SidebarGroupLabel>
        </SidebarGroup>

        <SidebarGroup className="border-y overflow-hidden p-0">
          <div className="flex gap-4 items-center py-5 px-3 min-w-0">
            <Avatar className="border border-gray-300">
              <AvatarImage src="" />
              <AvatarFallback>
                {user?.user_name
                  ? user?.user_name.slice(0, 2).toUpperCase()
                  : ""}
              </AvatarFallback>
            </Avatar>

            <div className="flex flex-col min-w-0 font-roboto text-base font-bold tracking-wider text-gray-700 transition-all duration-300">
              <span
                className="w-full truncate font-medium text-gray-600"
                title={user?.user_name}
              >
                {user?.user_name}
              </span>
              <span
                className="w-full truncate font-semibold text-gray-700"
                title={user?.role_name}
              >
                {user?.role_name}
              </span>
            </div>
          </div>
        </SidebarGroup>

        <SidebarGroupContent
          className={cn(
            "custom-scrollbar",
            "flex-1",
            "overflow-y-auto",
            "md:overflow-y-hidden md:hover:overflow-y-auto"
          )}
        >
          <SidebarMenu className="py-1 px-2">
            {sidebarState.map((menu, index) => {
              const parentActive =
                menu.name.toLowerCase() === "dashboard"
                  ? router.pathname === "/" || router.pathname === "/dashboard"
                  : menu.path === router.pathname ||
                    menu.children?.some((c) =>
                      router.asPath.startsWith(c.path)
                    );

              const hasChildren =
                Array.isArray(menu.children) && menu.children.length > 0;

              const Icon = menu.icon;

              return (
                <SidebarMenuItem key={index} className="mb-1">
                  {/* parent tanpa children */}
                  {!hasChildren && (
                    <SidebarMenuButton
                      asChild
                      className={cn(
                        "group/nosub",
                        baseButton,
                        parentActive ? activeStyle : inactiveStyle
                      )}
                    >
                      <Link
                        href={menu.path || "#"}
                        className="flex gap-2 items-center"
                      >
                        {Icon && (
                          <Icon
                            className={cn(
                              "w-5 h-5 md:w-[22px] md:h-[22px] flex-shrink-0",
                              parentActive
                                ? "text-red-600"
                                : "text-[#C5C5C5] group-hover/nosub:text-red-600"
                            )}
                          />
                        )}
                        <p className="capitalize text-[12px] md:text-[14px]">
                          {menu.name}
                        </p>
                      </Link>
                    </SidebarMenuButton>
                  )}

                  {/* parent dengan children */}
                  {hasChildren && (
                    <Collapsible asChild defaultOpen={parentActive}>
                      <div>
                        <CollapsibleTrigger asChild>
                          <SidebarMenuButton
                            className={cn(
                              baseButton,
                              parentActive ? activeStyle : inactiveStyle,
                              "group/withsub"
                            )}
                          >
                            {Icon && (
                              <Icon
                                className={cn(
                                  "w-5 h-5 md:w-[22px] md:h-[22px] flex-shrink-0",
                                  parentActive
                                    ? "text-red-600"
                                    : "text-[#C5C5C5] group-hover/withsub:text-red-600"
                                )}
                              />
                            )}
                            <p className="capitalize text-[12px] md:text-[14px]">
                              {menu.name}
                            </p>
                            <ChevronRight className="ml-auto transition-transform" />
                          </SidebarMenuButton>
                        </CollapsibleTrigger>

                        <CollapsibleContent>
                          <SidebarMenuSub className="mt-1 space-y-1 border-none">
                            {menu.children?.map((child, cIndex) => {
                              const active = router.asPath.startsWith(
                                child.path
                              );

                              return (
                                <Link
                                  key={cIndex}
                                  href={child.path}
                                  className={cn(
                                    "flex items-center p-3 rounded-md",
                                    active
                                      ? "text-[#FF0025] font-semibold"
                                      : "text-[#505D6D] hover:text-[#FF0025]"
                                  )}
                                >
                                  <span className="w-[22px] h-[22px] flex items-center justify-center">
                                    <span
                                      className={cn(
                                        "w-1.5 h-1.5 rounded-full",
                                        active ? "bg-[#FF0025]" : "bg-[#505D6D]"
                                      )}
                                    />
                                  </span>
                                  <span className="capitalize text-[12px] md:text-[14px]">
                                    {child.name}
                                  </span>
                                </Link>
                              );
                            })}
                          </SidebarMenuSub>
                        </CollapsibleContent>
                      </div>
                    </Collapsible>
                  )}
                </SidebarMenuItem>
              );
            })}
          </SidebarMenu>
        </SidebarGroupContent>
        <SidebarGroup className="border-t">
          <Footer />
        </SidebarGroup>
      </SidebarContent>
    </Sidebar>
  );
};

export default React.memo(AppSidebar);
